import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { solitaireAPI } from './solitaireApi';
import {
  SUITS, SUIT_GLYPH, applyMove, cardKey, fitsFoundation, isLegalMove, legalDestinations, normalizeDest, sameDest,
  sourceCards, starsFor, thresholdsOf, toFrom,
} from './solitaireModel';
import { impact, notify, selection } from '../common/haptics';
import HeroFigure from '../common/HeroFigure';
import PlayingCard, { CardBack } from './components/PlayingCard';
import SolitaireEndScreen from './components/SolitaireEndScreen';
import './solitaire.css';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { missionName } from '../../lair/utils/lairAssets';

// vertical offsets in card widths (--cw): face-down cards are packed tighter than face-up ones
const DOWN_STEP = 0.22;
const UP_STEP = 0.5;
const MIN_UP_STEP = 0.36; // keeps the large corner index (rank + suit) of every fanned card visible
const MAX_COLUMN_UNITS = 6.2; // the deepest card starts at most this many card widths below the column top
const CARD_UNITS = 1.4; // card height / width

function columnLayout(col) {
  const down = col?.down || 0;
  const up = col?.up?.length || 0;
  let upStep = UP_STEP;
  const downUnits = down * DOWN_STEP;
  if (up > 1 && downUnits + (up - 1) * upStep > MAX_COLUMN_UNITS) {
    upStep = Math.max(MIN_UP_STEP, (MAX_COLUMN_UNITS - downUnits) / (up - 1));
  }
  const height = downUnits + Math.max(0, up - 1) * upStep + CARD_UNITS;
  return { down, upStep, height };
}

const unit = (u) => `calc(var(--cw) * ${u.toFixed(3)})`;

const DRAG_THRESHOLD = 6; // px before a press becomes a drag (shorter = tap)
const RETURN_MS = 180; // illegal drop: the ghost glides back
const TOAST_MS = 1500;
const EDGE = 36; // px from the table edge where dragging scrolls it

/** Pile under the pointer: closest element with data-drop (the ghost has pointer-events: none). */
function dropTargetAt(x, y) {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
  const el = document.elementFromPoint(x, y);
  const t = el && typeof el.closest === 'function' ? el.closest('[data-drop]') : null;
  if (!t) return null;
  return { pile: t.getAttribute('data-drop'), index: Number(t.getAttribute('data-index')) };
}

/**
 * SOLITAIRE — Card Den mission mini-game (Klondike, draw-1, move budget).
 * Route: /lair/solitaire/:runId. Server-authoritative: the client sends DRAW / MOVE / AUTO / FINISH; legal MOVEs are
 * validated with solitaireModel and applied optimistically, then the server state replaces them (flips come from
 * the server; a rejection rolls back). Contract: solitaireApi.js.
 * Controls: drag a face-up card (a tableau card drags its whole run, also the waste top / a foundation top) onto a
 * pile — legal piles glow; an illegal drop glides back without a request. Quick tap on a card → its foundation if
 * it fits; tap the stock → draw.
 */
export default function SolitairePage() {
  const { t } = useTranslation();
  const { runId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'notfound' | 'error'
  const [state, setState] = useState(null);
  const [run, setRun] = useState(null);
  const [expired, setExpired] = useState(false);
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false); // a non-optimistic action (DRAW / AUTO / FINISH) is in flight
  const [toast, setToast] = useState(null);
  const [hero, setHero] = useState(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [drag, setDrag] = useState(null); // render snapshot of dragRef
  const [shakeKey, setShakeKey] = useState(null);

  const stateRef = useRef(null); // what is on screen (optimistic)
  const serverRef = useRef(null); // last state the server confirmed
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const toastTimer = useRef(null);
  const shakeTimer = useRef(null);
  const returnTimer = useRef(null);
  const runRetryRef = useRef(false);
  const queueRef = useRef(Promise.resolve());
  const pendingRef = useRef(0);
  const epochRef = useRef(0); // bumped on a rejection → queued actions are dropped
  const dragRef = useRef(null);
  const detachRef = useRef(null);
  const ghostRef = useRef(null);
  const tableRef = useRef(null);
  const interactiveRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(toastTimer.current);
      clearTimeout(shakeTimer.current);
      clearTimeout(returnTimer.current);
      if (detachRef.current) detachRef.current();
    };
  }, []);

  /** Light, auto-hiding, non-blocking hint (pointer-events: none) — never an alert. */
  const showToast = useCallback((text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const showState = useCallback((next) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const applyResponse = useCallback((data) => {
    const next = data?.state ?? null;
    if (next) {
      serverRef.current = next;
      showState(next);
    } else {
      setExpired(true);
    }
    if (data?.run !== undefined) setRun(data.run || null);
    if ((data?.events || []).some((ev) => ev?.e === 'timeout')) setExpired(true);
  }, [showState]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await solitaireAPI.getGame(runId);
      if (!mountedRef.current) return;
      applyResponse(data);
      setLoadError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      const status = e?.response?.status;
      if (!silent || !stateRef.current) setLoadError(status === 404 ? 'notfound' : 'error');
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [runId, applyResponse]);

  useEffect(() => { load(); }, [load]);

  // hero miniature (optional — the game works without it)
  useEffect(() => {
    let alive = true;
    solitaireAPI.getHero(runId).then((h) => { if (alive) setHero(h || null); }).catch(() => {});
    return () => { alive = false; };
  }, [runId]);

  const finished = !!state?.result || expired;
  const interactive = !!state && !finished && !busy;
  interactiveRef.current = interactive;

  // game over but the reward DTO hasn't arrived yet → re-read once
  useEffect(() => {
    if (!finished || run || runRetryRef.current) return undefined;
    runRetryRef.current = true;
    const t = setTimeout(() => load({ silent: true }), 1500);
    return () => clearTimeout(t);
  }, [finished, run, load]);

  const goBack = useCallback(() => navigate('/lair'), [navigate]);

  /**
   * Queues one action; actions reach the server strictly one after another.
   * `optimistic` (MOVE) — shown immediately, the table stays playable; otherwise the table is locked until the reply.
   * Reply → server state replaces the optimistic one once nothing else is pending. Rejection → roll back to the last
   * server state, drop the queued actions, soft toast (no alert) and a silent resync.
   */
  const enqueue = useCallback((body, { optimistic, quiet422 } = {}) => {
    setHint(null);
    if (optimistic) {
      showState(optimistic);
    } else {
      busyRef.current = true;
      setBusy(true);
    }
    pendingRef.current += 1;
    const epoch = epochRef.current;
    const job = queueRef.current.then(async () => {
      if (epoch !== epochRef.current || !mountedRef.current) return;
      try {
        const data = await solitaireAPI.act(runId, body);
        if (!mountedRef.current) return;
        const next = data?.state;
        if (pendingRef.current === 1 || !next || next.result) applyResponse(data);
        else {
          serverRef.current = next; // more optimistic moves are queued — keep showing them
          if (data?.run !== undefined) setRun(data.run || null);
        }
        if (next?.result === 'WIN') notify('success');
        else if (next?.result === 'LOSE') notify('error');
      } catch (e) {
        if (!mountedRef.current) return;
        epochRef.current += 1;
        if (serverRef.current) showState(serverRef.current);
        const status = e?.response?.status;
        if (status === 422) {
          notify('warning');
          if (!serverRef.current?.result) showToast(quiet422 || i18n.t('solitaire.cant_move', "Can't move there"));
          if (body.action === 'MOVE' || body.action === 'DRAW') await load({ silent: true });
        } else if (status === 404) {
          showToast(i18n.t('game.run_not_found', 'Run not found'));
        } else {
          showToast(i18n.t('game.connection_problem', 'Connection problem — try again'));
          await load({ silent: true });
        }
      }
    }).finally(() => {
      pendingRef.current -= 1;
      if (!optimistic) {
        busyRef.current = false;
        if (mountedRef.current) setBusy(false);
      }
    });
    queueRef.current = job;
    return job;
  }, [runId, applyResponse, showState, showToast, load]);

  /** Validated MOVE: illegal → false and no request; legal → cards move now, the request follows. */
  const doMove = useCallback((src, dest) => {
    const cur = stateRef.current;
    const next = applyMove(cur, src, dest);
    if (!next) return false;
    const to = normalizeDest(cur, src, dest);
    if (to.pile === 'FOUNDATION') impact('light');
    else selection();
    enqueue({ action: 'MOVE', from: toFrom(src), to }, { optimistic: next });
    return true;
  }, [enqueue]);

  const shake = useCallback((key) => {
    setShakeKey(key);
    clearTimeout(shakeTimer.current);
    shakeTimer.current = setTimeout(() => setShakeKey(null), 320);
  }, []);

  /** Quick tap on a face-up card: to its foundation if it fits, otherwise a tiny shake. */
  const onTap = useCallback((src) => {
    const cur = stateRef.current;
    const cards = sourceCards(cur, src);
    if (!cards.length || src.pile === 'FOUNDATION') return;
    const top = cards.length === 1 ? cards[0] : null;
    if (top && fitsFoundation(top, cur.foundations) && doMove(src, { pile: 'FOUNDATION', index: SUITS.indexOf(top.s) })) return;
    shake(cardKey(cards[0]));
  }, [doMove, shake]);

  const endDrag = useCallback(() => {
    if (detachRef.current) detachRef.current();
    detachRef.current = null;
  }, []);

  /** Press on a face-up card → track the pointer on window; past the threshold it becomes a drag. */
  const onCardPointerDown = useCallback((src) => (e) => {
    if (!interactiveRef.current || dragRef.current) return;
    if (e.button != null && e.button > 0) return;
    const cards = sourceCards(stateRef.current, src);
    if (!cards.length) return;
    if (e.cancelable) e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width || 48;
    let step = 0;
    if (src.pile === 'TABLEAU') step = columnLayout(stateRef.current.tableau[src.index]).upStep * width;
    const d = {
      pointerId: e.pointerId,
      src,
      cards,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
      originX: rect.left,
      originY: rect.top,
      width,
      step,
      active: false,
      legal: [],
      over: null,
      overLegal: false,
      returning: false,
    };
    dragRef.current = d;

    const sameKey = (a, b) => (a ? `${a.pile}:${a.index}` : '') === (b ? `${b.pile}:${b.index}` : '');

    const onMove = (ev) => {
      const cur = dragRef.current;
      if (!cur || cur.returning || (ev.pointerId != null && cur.pointerId != null && ev.pointerId !== cur.pointerId)) return;
      cur.x = ev.clientX;
      cur.y = ev.clientY;
      let changed = false;
      if (!cur.active) {
        if (Math.hypot(cur.x - cur.startX, cur.y - cur.startY) < DRAG_THRESHOLD) return;
        cur.active = true;
        cur.legal = legalDestinations(stateRef.current, cur.src);
        changed = true;
        setHint(null);
        selection();
      }
      if (ev.cancelable) ev.preventDefault();
      const raw = dropTargetAt(cur.x, cur.y);
      const over = raw ? (normalizeDest(stateRef.current, cur.src, raw) || raw) : null;
      if (!sameKey(over, cur.over)) {
        cur.over = over;
        cur.overLegal = !!over && isLegalMove(stateRef.current, cur.src, over);
        changed = true;
      }
      // long columns: scroll the table while the finger is near its edge
      const table = tableRef.current;
      if (table && table.scrollHeight > table.clientHeight) {
        const r = table.getBoundingClientRect();
        if (cur.y > r.bottom - EDGE) table.scrollTop += 10;
        else if (cur.y < r.top + EDGE) table.scrollTop -= 10;
      }
      if (changed) setDrag({ ...cur });
      else if (ghostRef.current) {
        ghostRef.current.style.transform = `translate3d(${cur.x - cur.offX}px, ${cur.y - cur.offY}px, 0)`;
      }
    };

    const finish = (ev, cancelled) => {
      const cur = dragRef.current;
      if (!cur || cur.returning) return;
      if (ev && ev.pointerId != null && cur.pointerId != null && ev.pointerId !== cur.pointerId) return;
      endDrag();
      if (!cur.active) {
        dragRef.current = null;
        if (!cancelled) onTap(cur.src);
        return;
      }
      if (!cancelled && cur.over && cur.overLegal && doMove(cur.src, cur.over)) {
        dragRef.current = null;
        setDrag(null);
        return;
      }
      // illegal / outside → glide back, no request
      cur.returning = true;
      cur.x = cur.originX + cur.offX;
      cur.y = cur.originY + cur.offY;
      cur.over = null;
      cur.overLegal = false;
      setDrag({ ...cur });
      clearTimeout(returnTimer.current);
      returnTimer.current = setTimeout(() => {
        dragRef.current = null;
        if (mountedRef.current) setDrag(null);
      }, RETURN_MS);
    };
    const onUp = (ev) => finish(ev, false);
    const onCancel = (ev) => finish(ev, true);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    detachRef.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [doMove, onTap, endDrag]);

  const onStockTap = useCallback(() => {
    if (!interactive || dragRef.current) return;
    if (!state.stock && !state.wasteCount) {
      showToast(i18n.t('solitaire.stock_empty', 'The stock is empty'));
      return;
    }
    enqueue({ action: 'DRAW' });
  }, [interactive, state, enqueue, showToast]);

  const onHint = useCallback(async () => {
    if (!interactive) return;
    await queueRef.current; // ask about the confirmed table, not a half-sent one
    try {
      const data = await solitaireAPI.getGame(runId, { hint: true });
      if (!mountedRef.current) return;
      const h = data?.state?.hint;
      if (h) {
        selection();
        setHint(h);
      } else {
        showToast(i18n.t('solitaire.no_useful_moves', 'No useful moves left — finish the game'));
      }
    } catch (e) {
      showToast(i18n.t('solitaire.hint_unavailable', 'Hint is unavailable'));
    }
  }, [interactive, runId, showToast]);

  const onAuto = useCallback(() => {
    if (!interactive) return;
    enqueue({ action: 'AUTO' }, { quiet422: i18n.t('solitaire.nothing_up', 'Nothing can go up safely') });
  }, [interactive, enqueue]);

  const onFinish = useCallback(async () => {
    setConfirmFinish(false);
    await enqueue({ action: 'FINISH' });
  }, [enqueue]);

  // ── render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="sol-page sol-page--center">
        <div className="sol-spinner" aria-label={t('common.loading', 'Loading…')} />
        <div className="sol-muted">{t('solitaire.shuffling', 'Shuffling the deck…')}</div>
      </div>
    );
  }

  if (loadError || (!state && !run && !expired)) {
    const notFound = loadError === 'notfound';
    return (
      <div className="sol-page sol-page--center">
        <div className="sol-error-title">{notFound ? t('game.run_not_found', 'Run not found') : t('solitaire.load_failed', "Couldn't open the card den")}</div>
        <div className="sol-muted">{notFound ? t('game.no_longer_exists', 'This game no longer exists.') : t('game.check_connection', 'Check your connection and try again.')}</div>
        <div className="sol-error-btns">
          {!notFound && <button type="button" className="app-btn app-btn--blue" onClick={() => load()}>{t('common.retry', 'Retry')}</button>}
          <button type="button" className="app-btn app-btn--green" onClick={goBack}>{t('mission.back_to_lair', 'Back to Lair')}</button>
        </div>
      </div>
    );
  }

  const thresholds = thresholdsOf(state);
  const foundation = state?.foundationCount ?? 0;
  const stars = starsFor(foundation, thresholds);
  const movesLeft = state?.movesLeft;
  const low = movesLeft != null && movesLeft <= 10;
  const showEnd = finished;

  const hintFrom = hint?.action === 'MOVE' ? hint.from : null;
  const hintTo = hint?.action === 'MOVE' ? hint.to : null;
  const hintDraw = hint?.action === 'DRAW';
  const hintDestIndex = hintTo?.pile === 'FOUNDATION' && hintFrom
    ? SUITS.indexOf(sourceCards(state, hintFrom)[0]?.s)
    : hintTo?.index;
  const isHintDest = (pile, index) => !!hintTo && hintTo.pile === pile && hintDestIndex === index;

  // drag & drop highlights
  const dragging = !!drag?.active;
  const dragSrc = dragging ? drag.src : null;
  const isLegal = (dest) => dragging && !drag.returning && drag.legal.some((d) => sameDest(d, dest));
  const isOver = (dest) => dragging && !!drag.over && sameDest(drag.over, dest);
  const overCls = (dest) => {
    if (!isOver(dest)) return '';
    return drag.overLegal ? ' is-over' : ' is-over-bad';
  };
  const inDrag = (pile, index, cardIndex) => {
    if (!dragSrc || dragSrc.pile !== pile) return false;
    if (pile === 'WASTE') return true;
    if (dragSrc.index !== index) return false;
    return pile === 'FOUNDATION' || cardIndex >= dragSrc.cardIndex;
  };

  const waste = state?.waste || [];

  const ghost = dragging && typeof document !== 'undefined' ? createPortal(
    <div
      ref={ghostRef}
      className={`sol-ghost${drag.returning ? ' is-returning' : ''}`}
      data-testid="drag-ghost"
      style={{
        '--cw': `${drag.width}px`,
        '--ch': `${drag.width * 1.4}px`,
        width: drag.width,
        height: drag.width * 1.4 + drag.step * (drag.cards.length - 1),
        transform: `translate3d(${drag.x - drag.offX}px, ${drag.y - drag.offY}px, 0)`,
      }}
    >
      {drag.cards.map((c, i) => (
        <PlayingCard key={cardKey(c)} card={c} style={{ position: 'absolute', left: 0, top: i * drag.step }} testId={`ghost-${cardKey(c)}`} />
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <div className="sol-page">
      <div className="sol-topbar">
        <button type="button" className="sol-back" onClick={goBack} aria-label={t('common.back', 'Back')}>‹</button>
        <div className="sol-title">
          <span className="sol-title__kicker">🃏 {t('solitaire.kicker', 'Solitaire')}</span>
          <span className="sol-title__name">{missionName(run, t) || t('solitaire.default_title', 'Card Den')}</span>
        </div>
        <div className={`sol-moves${low ? ' is-low' : ''}`} data-testid="hud-moves" aria-label={t('game.moves_left', 'Moves left')}>
          <span className="sol-moves__n">{movesLeft ?? '–'}</span>
          <span className="sol-moves__of">/{state?.movesTotal ?? '–'} {t('game.moves', 'moves')}</span>
        </div>
      </div>

      <div className="sol-progress">
        <div className="sol-hero" data-testid="sol-hero">
          <HeroFigure look={hero?.look} character={hero?.character} size={46} facing={1} pose={stars ? 'jump' : 'idle'} />
        </div>
        <div className="sol-progress__main">
          <div className="sol-progress__row">
            <span className="sol-progress__label">{t('solitaire.foundations', 'Foundations')}</span>
            <span className="sol-progress__value" data-testid="hud-foundation">{foundation}<small>/52</small></span>
          </div>
          <div className="sol-bar" role="progressbar" aria-valuemin={0} aria-valuemax={52} aria-valuenow={foundation}>
            <div className="sol-bar__fill" style={{ width: `${(foundation / 52) * 100}%` }} />
            {thresholds.map((th, i) => (
              <span
                key={th || i}
                className={`sol-bar__star${stars > i ? ' is-on' : ''}`}
                style={{ left: `${(th / 52) * 100}%` }}
                title={t('solitaire.star_at', '{{n}}★ at {{cards}} cards', { n: i + 1, cards: th })}
                data-testid={`hud-star-${i + 1}`}
              >
                ★
              </span>
            ))}
          </div>
          {state?.movesBonus ? (
            <div className="sol-progress__bonus" data-testid="hud-bonus">
              {state.movesBonus > 0
                ? t('solitaire.hero_bonus', 'Hero bonus +{{n}} moves', { n: state.movesBonus })
                : t('solitaire.weak_hero', 'Weak hero {{n}} moves', { n: state.movesBonus })}
            </div>
          ) : null}
        </div>
      </div>

      <div
        ref={tableRef}
        className={`sol-table${interactive ? '' : ' is-disabled'}${dragging ? ' is-dragging' : ''}`}
        data-testid="sol-table"
      >
        {/* stock · waste · foundations — on the same 7-column grid as the tableau */}
        <div className="sol-row">
          <button
            type="button"
            className={`sol-slot sol-stock${state?.stock ? ' has-cards' : ''}${hintDraw ? ' is-hinted' : ''}`}
            onClick={onStockTap}
            data-testid="stock"
            aria-label={state?.stock
              ? t('solitaire.stock_aria', 'Stock, {{n}} cards — draw', { n: state.stock })
              : t('solitaire.recycle_aria', 'Turn the waste over')}
          >
            {state?.stock ? <CardBack testId="stock-back" /> : null}
            {state?.stock ? <span className="sol-stock__n">{state.stock}</span> : <span className="sol-stock__recycle">↻</span>}
          </button>
          <div className="sol-waste" data-testid="waste">
            {waste.map((c, i) => {
              const top = i === waste.length - 1;
              const style = { left: unit(i * 0.38), zIndex: i + 1 };
              return top ? (
                <PlayingCard
                  key={cardKey(c)}
                  card={c}
                  style={style}
                  draggable={interactive}
                  dragging={inDrag('WASTE')}
                  shake={shakeKey === cardKey(c)}
                  hinted={hintFrom?.pile === 'WASTE'}
                  onPointerDown={onCardPointerDown({ pile: 'WASTE' })}
                />
              ) : (
                <div key={cardKey(c)} className="sol-waste__under" style={style}>
                  <PlayingCard card={c} testId={`waste-under-${cardKey(c)}`} />
                </div>
              );
            })}
            {!waste.length && <div className="sol-slot sol-slot--empty" />}
          </div>
          {SUITS.map((s, i) => {
            const top = state?.foundations?.[i];
            const dest = { pile: 'FOUNDATION', index: i };
            const legalHere = isLegal(dest);
            return (
              <div
                key={s}
                className={`sol-slot sol-foundation${legalHere ? ' is-legal' : ''}${overCls(dest)}${isHintDest('FOUNDATION', i) ? ' is-hinted' : ''}`}
                data-testid={`foundation-${i}`}
                data-drop="FOUNDATION"
                data-index={i}
                aria-label={`${s} foundation`}
              >
                {top && top.r > 1 && (
                  <PlayingCard card={{ r: top.r - 1, s: top.s }} testId={`foundation-under-${i}`} />
                )}
                {top ? (
                  <PlayingCard
                    card={top}
                    draggable={interactive}
                    dragging={inDrag('FOUNDATION', i)}
                    legal={legalHere}
                    hinted={isHintDest('FOUNDATION', i) || (hintFrom?.pile === 'FOUNDATION' && hintFrom.index === i)}
                    onPointerDown={onCardPointerDown({ pile: 'FOUNDATION', index: i })}
                    testId={`foundation-top-${i}`}
                  />
                ) : (
                  <span className="sol-foundation__suit">{SUIT_GLYPH[s]}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="sol-tableau">
          {(state?.tableau || []).map((col, ci) => {
            const { down, upStep, height } = columnLayout(col);
            const dest = { pile: 'TABLEAU', index: ci };
            const legalHere = isLegal(dest);
            const hintedDest = isHintDest('TABLEAU', ci);
            return (
              <div
                key={ci}
                className={`sol-col${legalHere ? ' is-legal' : ''}${overCls(dest)}${hintedDest ? ' is-hinted' : ''}`}
                style={{ minHeight: unit(height) }}
                data-testid={`col-${ci}`}
                data-drop="TABLEAU"
                data-index={ci}
                data-down={down}
              >
                {!col.up.length && !down && <div className="sol-slot sol-slot--empty sol-col__empty">K</div>}
                {Array.from({ length: down }, (_, k) => (
                  <div key={`d${k}`} className="sol-col__pos" style={{ top: unit(k * DOWN_STEP) }}>
                    <CardBack />
                  </div>
                ))}
                {col.up.map((c, k) => {
                  const cardIndex = down + k;
                  const inHint = hintFrom?.pile === 'TABLEAU' && hintFrom.index === ci
                    && cardIndex >= (hintFrom.cardIndex ?? col.down + col.up.length - 1);
                  const isTop = k === col.up.length - 1;
                  return (
                    <div key={cardKey(c)} className="sol-col__pos" style={{ top: unit(down * DOWN_STEP + k * upStep), zIndex: 10 + k }}>
                      <PlayingCard
                        card={c}
                        draggable={interactive}
                        dragging={inDrag('TABLEAU', ci, cardIndex)}
                        shake={shakeKey === cardKey(c)}
                        hinted={inHint || (isTop && hintedDest)}
                        legal={isTop && legalHere}
                        over={isTop && isOver(dest) && drag.overLegal}
                        onPointerDown={onCardPointerDown({ pile: 'TABLEAU', index: ci, cardIndex })}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      {toast && <div className="sol-toast" role="status" data-testid="sol-toast">{toast}</div>}
      {ghost}

      <div className="sol-actions">
        <button type="button" className="sol-action" onClick={onHint} disabled={!interactive} data-testid="btn-hint">💡 {t('solitaire.hint', 'Hint')}</button>
        <button type="button" className="sol-action" onClick={onAuto} disabled={!interactive} data-testid="btn-auto">⇪ {t('solitaire.auto', 'Auto')}</button>
        <button
          type="button"
          className="sol-action sol-action--finish"
          onClick={() => setConfirmFinish(true)}
          disabled={!interactive}
          data-testid="btn-finish"
        >
          ⚑ {t('solitaire.finish', 'Finish')}
        </button>
      </div>

      {confirmFinish && !finished && (
        <div className="sol-confirm" role="dialog" aria-label={t('solitaire.finish_aria', 'Finish the game')} data-testid="finish-confirm">
          <div className="sol-confirm__card">
            <div className="sol-confirm__title">{t('solitaire.finish_q', 'Finish now?')}</div>
            <div className="sol-confirm__text">
              {stars
                ? t('solitaire.finish_stars', '{{n}} cards are on the foundations — that is {{stars}}.', { n: foundation, stars: '★'.repeat(stars) })
                : t('solitaire.finish_loss', '{{n}} cards are on the foundations — you need {{need}} for ★, this will count as a loss.', { n: foundation, need: thresholds[0] })}
            </div>
            <div className="sol-confirm__btns">
              <button type="button" className="app-btn app-btn--blue" onClick={() => setConfirmFinish(false)}>{t('solitaire.keep_playing', 'Keep playing')}</button>
              <button type="button" className="app-btn app-btn--green" onClick={onFinish} data-testid="finish-yes">{t('solitaire.finish', 'Finish')}</button>
            </div>
          </div>
        </div>
      )}

      {showEnd && (
        <SolitaireEndScreen
          result={state?.result || 'LOSE'}
          stars={state?.stars ?? run?.stars ?? 0}
          foundation={foundation}
          run={run}
          timedOut={expired && state?.result !== 'WIN'}
          onBack={goBack}
        />
      )}
    </div>
  );
}
